import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { useCRMData } from '../context/CRMDataContext';
import { formatCurrency } from '../utils/helpers';
import ForecastMetric from '../components/forecast/ForecastMetric';
import ForecastBarChart from '../components/forecast/ForecastBarChart';

const toMonthKey = date => {
  const value = date ? new Date(date) : new Date();
  if (Number.isNaN(value.getTime())) return '';
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}`;
};

const monthLabel = monthKey => {
  const [year, month] = monthKey.split('-');
  return new Intl.DateTimeFormat('nb-NO', { month: 'short', year: 'numeric' })
    .format(new Date(Number(year), Number(month) - 1, 1));
};

const getDealStatus = deal => String(deal?.status || '').toLowerCase();

const isWonDeal = deal => {
  const status = getDealStatus(deal);

  return (
    status === 'vunnet' ||
    status === 'customer' ||
    status === 'closed_won' ||
    deal?.isCustomer === true ||
    deal?.convertedToCustomer === true ||
    String(deal?.customerStatus || '').toLowerCase() === 'active'
  );
};

const isActiveDeal = (deal) => {
  const status = getDealStatus(deal);

  return (
    Boolean(deal) &&
    deal.archived !== true &&
    deal.deleted !== true &&
    deal.is_deleted !== true &&
    status !== 'arkivert' &&
    status !== 'slettet' &&
    status !== 'deleted' &&
    !isWonDeal(deal)
  );
};

const isPipelineDeal = (deal) => isActiveDeal(deal) && Boolean(deal.stage_id);

const DealForecast = () => {
  const { deals, companies } = useCRMData();
  const [period, setPeriod] = useState('all');

  const safeDeals = Array.isArray(deals) ? deals : [];
  const safeCompanies = Array.isArray(companies) ? companies : [];

  const forecast = useMemo(() => {
    const openDeals = safeDeals.filter(isPipelineDeal);
    const filteredDeals = period === 'all'
      ? openDeals
      : openDeals.filter(deal => toMonthKey(deal.expected_close_date) === period);

    const totalValue = filteredDeals.reduce((sum, deal) => sum + (Number(deal.value) || 0), 0);
    const weightedValue = filteredDeals.reduce(
      (sum, deal) => sum + ((Number(deal.value) || 0) * (Number(deal.probability) || 0) / 100),
      0
    );
    const commitValue = filteredDeals
      .filter(deal => Number(deal.probability) >= 75)
      .reduce((sum, deal) => sum + (Number(deal.value) || 0), 0);

    const monthlyMap = filteredDeals.reduce((map, deal) => {
      const key = toMonthKey(deal.expected_close_date);
      if (!map[key]) map[key] = { value: 0, count: 0 };
      map[key].value += Number(deal.value) || 0;
      map[key].count += 1;
      return map;
    }, {});

    const months = Object.entries(monthlyMap)
      .sort(([first], [second]) => first.localeCompare(second))
      .slice(0, 8)
      .map(([key, data]) => ({
        label: monthLabel(key),
        value: data.value,
        count: data.count,
        formattedValue: formatCurrency(data.value, true),
        color: 'bg-blue-600'
      }));

    const confidence = [
      { label: 'Commit (75–100%)', value: filteredDeals.filter(d => Number(d.probability) >= 75).reduce((s, d) => s + (Number(d.value) || 0), 0), color: 'bg-emerald-500' },
      { label: 'Best case (40–74%)', value: filteredDeals.filter(d => Number(d.probability) >= 40 && Number(d.probability) < 75).reduce((s, d) => s + (Number(d.value) || 0), 0), color: 'bg-amber-500' },
      { label: 'Pipeline (0–39%)', value: filteredDeals.filter(d => Number(d.probability) < 40).reduce((s, d) => s + (Number(d.value) || 0), 0), color: 'bg-slate-400' }
    ].map(row => ({
      ...row,
      count: filteredDeals.filter(deal => {
        const probability = Number(deal.probability) || 0;
        return row.label.startsWith('Commit') ? probability >= 75
          : row.label.startsWith('Best') ? probability >= 40 && probability < 75
          : probability < 40;
      }).length,
      formattedValue: formatCurrency(row.value, true)
    }));

    return { filteredDeals, totalValue, weightedValue, commitValue, months, confidence };
  }, [period, safeDeals]);

  const periods = [...new Set(safeDeals
    .filter(isPipelineDeal)
    .map(deal => toMonthKey(deal.expected_close_date))
    .filter(Boolean))]
    .sort();

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 md:px-8">
      <div className="mx-auto max-w-[1500px] space-y-8">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-blue-600">Salg & styring</p>
            <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-900">Deal Forecasting</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-500">
              Forventet omsetning basert på åpne deals, verdi og sannsynlighet.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={period}
              onChange={event => setPeriod(event.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm outline-none"
            >
              <option value="all">Alle måneder</option>
              {periods.map(value => <option key={value} value={value}>{monthLabel(value)}</option>)}
            </select>
            <Link to="/pipeline" className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-800">
              Åpne pipeline
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <ForecastMetric label="Åpen pipeline" value={formatCurrency(forecast.totalValue, true)} detail={`${forecast.filteredDeals.length} aktive deals`} tone="blue" />
          <ForecastMetric label="Vektet forecast" value={formatCurrency(forecast.weightedValue, true)} detail="Verdi × sannsynlighet" tone="emerald" />
          <ForecastMetric label="Commit" value={formatCurrency(forecast.commitValue, true)} detail="Deals med minst 75% sannsynlighet" tone="amber" />
          <ForecastMetric label="Gjennomsnittlig deal" value={formatCurrency(forecast.filteredDeals.length ? forecast.totalValue / forecast.filteredDeals.length : 0, true)} detail="På aktive deals" tone="slate" />
        </div>

        <div className="grid grid-cols-1 gap-8 xl:grid-cols-2">
          <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm md:p-8">
            <div className="mb-8 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-black text-slate-900">Forecast per måned</h2>
                <p className="mt-1 text-xs text-slate-400">Basert på forventet lukkedato</p>
              </div>
              <SafeIcon icon="📈" className="text-2xl" />
            </div>
            {forecast.months.length ? <ForecastBarChart rows={forecast.months} /> : <EmptyForecast />}
          </section>

          <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm md:p-8">
            <div className="mb-8 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-black text-slate-900">Sannsynlighetsfordeling</h2>
                <p className="mt-1 text-xs text-slate-400">Hvor trygg er pipelineverdien?</p>
              </div>
              <SafeIcon icon="🎯" className="text-2xl" />
            </div>
            <ForecastBarChart rows={forecast.confidence} />
          </section>
        </div>

        <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-5 md:px-8">
            <div>
              <h2 className="text-lg font-black text-slate-900">Største muligheter</h2>
              <p className="mt-1 text-xs text-slate-400">Prioriter deals med høy verdi og tydelig sannsynlighet</p>
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-500">{forecast.filteredDeals.length} aktive</span>
          </div>
          {forecast.filteredDeals.length ? (
            <div className="divide-y divide-slate-100">
              {[...forecast.filteredDeals]
                .sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0))
                .slice(0, 8)
                .map(deal => (
                  <div key={deal.deal_id || deal.id} className="grid grid-cols-1 gap-3 px-6 py-4 md:grid-cols-[1.5fr_1fr_120px_120px] md:items-center md:px-8">
                    <div>
                      <p className="font-bold text-slate-900">{deal.title || 'Uten tittel'}</p>
                      <p className="text-xs text-slate-400">
                        {safeCompanies.find(company => company.company_id === deal.company_id)?.name || 'Ukjent selskap'}
                      </p>
                    </div>
                    <div className="text-sm font-bold text-slate-700">{deal.expected_close_date || 'Ingen dato'}</div>
                    <div className="text-sm font-black text-slate-900">{formatCurrency(Number(deal.value) || 0, true)}</div>
                    <div className="text-sm font-black text-blue-600">{Number(deal.probability) || 0}%</div>
                  </div>
                ))}
            </div>
          ) : <EmptyForecast />}
        </section>
      </div>
    </div>
  );
};

const EmptyForecast = () => (
  <div className="rounded-2xl border-2 border-dashed border-slate-200 py-12 text-center">
    <SafeIcon icon="📊" className="mb-3 text-3xl opacity-60" />
    <p className="text-sm font-bold text-slate-500">Ingen aktive deals å analysere ennå.</p>
    <p className="mt-1 text-xs text-slate-400">Opprett deals i pipeline for å bygge forecastet.</p>
  </div>
);

export default DealForecast;